
import {
    HtaArray,
    HtaConnectorCode,
    HtaConnectorViewProps,
} from './types'

import {
    captionTitle, cellInner, copyButtonWrapper, styledHtaConnectorView,
} from './styles/HtaConnectorView'

import {CopyButton} from 'components/AudienceMetadata/components/CopyButton'

import {LockClosedIcon} from '../Icon'
import React from 'react'

import {buttonContainer} from 'components/AudienceMetadata/styles/AudienceMetadata'
import {styled} from 'stitches/config'
import {toast} from 'components/Toast'

import {useCopyToClipboard} from 'hooks/clipboard'
import {useTranslation} from 'react-i18next'

const StyledHtaConnectorView = styled('div', styledHtaConnectorView)
const StyledButtonContainer = styled('div', buttonContainer)
const StyledTableCaption = styled('caption', {[`& ${StyledButtonContainer}`]: {visibility: 'visible'}})
const StyledCaptionTitle = styled('div', captionTitle)
const StyledCopyButton = styled('div', copyButtonWrapper)

const StyledCellInner = styled('div', {
    ...cellInner,
    '&:hover': {
        [`${StyledCopyButton}`]: {visibility: 'visible'},
        backgroundColor: '$metadataItemBackgroundHover',
        borderRadius: '$metadataItemBorder',
    },
})

export const blockHtaKeys = [
    'ICD-10',
    'ICPC-2',
    'patientInitialInfo',
    'patientCareNeeds',
    'patientHas',
    'patientHasNot',
    'patientDoesNotKnow',
    'triage',
    'urgencyRecommendation',
    'selfCareInstructions',
]

const getId = (s: string) => {
    let h = 0

    for (let i = 0; i < s.length; i += 1) {
        // eslint-disable-next-line no-bitwise
        h = Math.imul(31, h) + s.charCodeAt(i) | 0
    }

    return h
}

const isBlockKey = (key: string) => blockHtaKeys.indexOf(key) !== -1

const renderIcpc = (icpc: HtaConnectorCode): React.ReactElement => <li key={icpc.code}>{icpc.code}: {icpc.title}</li>

const renderItem = (key: string, item: string | string[] | HtaConnectorCode[]): React.ReactElement => {
    if (!item.length) {
        return <div>--</div>
    }

    if (Array.isArray(item)) {
        switch (key) {
            case 'ICD-10':
            case 'ICPC-2':
                return <ul className='icpc'>{item.map((icpc) => renderIcpc(icpc as HtaConnectorCode))}</ul>
            default:
                return <ul className='array'>{item.map((contentItem, i) => <li key={getId(i + JSON.stringify(contentItem))}>{contentItem as string}</li>)}</ul>
        }
    }

    return <span className='value'>{item}</span>
}

const prepareCopyString = (hta: HtaArray[]) => hta.reduce((acc, current) => {
    const [key, item, title] = current

    return `${acc}${'\n\n'}${title}: ${(() => {
        if (!item.length) {
            return '\n--'
        }

        if (Array.isArray(item)) {
            switch (key) {
                case 'ICD-10':
                case 'ICPC-2':
                    // eslint-disable-next-line no-extra-parens
                    return (item as HtaConnectorCode[]).reduce(
                        (acc_nested, code, code_index) => `${acc_nested} ${code.code}: ${code.title}${code_index + 1 < item.length ? ',' : ''}`,
                        '\n',
                    )
                default:
                    // eslint-disable-next-line no-extra-parens
                    return (item as string[]).reduce((acc_nested, listItem) => `${acc_nested}\n - ${listItem}`, '')
            }
        }

        return item
    })()}`
}, '')


const HtaConnectorView = (props: HtaConnectorViewProps): React.ReactElement => {
    const {hta} = props
    const {t} = useTranslation()

    const clipboard = useCopyToClipboard({
        onSuccess: (key) => {
            toast.success(t('Copied: {{key}}', {key}))
        },
    })

    const handleCopy = (toCopy: string, copyKey: string) => () => {
        clipboard.copy(toCopy, copyKey)
    }

    const htaConnectorHeader = t('Summary of client\'s answers to questionnaire')
    const htaStringToCopy = prepareCopyString(hta)
    return (
        <StyledHtaConnectorView>
            <table>
                <StyledTableCaption title={htaConnectorHeader}>
                    <StyledCaptionTitle><LockClosedIcon />{htaConnectorHeader}</StyledCaptionTitle>
                    <StyledButtonContainer>
                        <CopyButton
                            currentKey=''
                            currentValue={{[htaConnectorHeader]: htaStringToCopy}}
                            showCopyAll={true}
                        />
                    </StyledButtonContainer>
                </StyledTableCaption>
                <tbody>
                    {hta.map((item) => (
                        <tr key={item[0]}>
                            <td>
                                <StyledCellInner onClick={handleCopy(prepareCopyString([item]), item[2])}>
                                    <div>
                                        <div>
                                            <span className={isBlockKey(item[0]) ? 'blockKey' : 'key'} >{item[2]}: </span>
                                            {typeof item[1] === 'string' ? renderItem(item[0], item[1]) : null}
                                        </div>
                                        <StyledCopyButton>
                                            <CopyButton currentKey={item[2]} currentValue={prepareCopyString([item])} showCopyAll={true} />
                                        </StyledCopyButton>
                                    </div>
                                    {typeof item[1] === 'string' ? null : renderItem(item[0], item[1])}
                                </StyledCellInner>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </StyledHtaConnectorView>
    )
}

export default HtaConnectorView
